import Layout from "@/components/Layout";

const TrainingTerms = () => {
  return (
    <Layout>
      <section className="py-16 lg:py-20" id="training-terms">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
                Training Terms & Conditions
              </h1>
              <p className="text-muted-foreground">
                Last updated: 26 September, 2025.
              </p>
            </div>

            <div className="prose prose-lg max-w-none space-y-8">
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  1. Registration and Enrollment
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Registration for training programs is confirmed upon receipt
                    of full payment or approved payment arrangement. Places are
                    allocated on a first-come, first-served basis.
                  </p>
                  <p>
                    Corporate bookings may require additional documentation
                    including purchase orders and participant lists.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  2. Payment Requirements
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Individual Participants:</strong> Full payment is
                    required at registration unless alternative arrangements are
                    made.
                  </p>
                  <p>
                    <strong>Corporate Clients:</strong> Payment terms may be
                    negotiated based on the scope and duration of training
                    programs.
                  </p>
                  <p>
                    <strong>Accepted Payment Methods:</strong> Bank transfer,
                    mobile money, credit cards, and corporate invoicing.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  3. Attendance Requirements
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Participants must attend a minimum of 80% of training
                    sessions to receive certification. Late arrivals and early
                    departures may affect certification eligibility.
                  </p>
                  <p>
                    For online training, active participation in virtual
                    sessions is required and monitored.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  4. Cancellation Policy
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>By Participants:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      30+ days before start: Full refund minus administrative
                      fee
                    </li>
                    <li>15-29 days before start: 75% refund</li>
                    <li>7-14 days before start: 50% refund</li>
                    <li>
                      Less than 7 days: No refund (transfer to future program
                      allowed)
                    </li>
                  </ul>
                  <p>
                    <strong>By TCLA-X:</strong> We reserve the right to cancel
                    programs due to insufficient enrollment or unforeseen
                    circumstances. Full refunds will be provided.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  5. Transfers and Substitutions
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Participants may transfer to another program session subject
                    to availability. Transfer requests must be made at least 48
                    hours before the program start date.
                  </p>
                  <p>
                    Corporate clients may substitute participants up to 24 hours
                    before program commencement.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  6. Materials and Resources
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Training materials, handouts, and digital resources are
                    provided for program participants. These materials are for
                    personal use only and may not be reproduced or distributed
                    without permission.
                  </p>
                  <p>
                    Participants are responsible for bringing their own devices
                    for technology-based training.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  7. Certification Requirements
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Certificates are awarded upon successful completion of
                    training requirements including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Minimum 80% attendance</li>
                    <li>Completion of all assignments and assessments</li>
                    <li>Active participation in training activities</li>
                    <li>
                      Satisfactory performance on final evaluations (where
                      applicable)
                    </li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  8. Code of Conduct
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Participants are expected to maintain professional behavior
                    throughout training programs. This includes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      Respectful interaction with trainers and fellow
                      participants
                    </li>
                    <li>Punctuality and active engagement</li>
                    <li>Appropriate dress code for in-person sessions</li>
                    <li>Professional communication in online platforms</li>
                    <li>
                      Confidentiality regarding sensitive business information
                      shared during training
                    </li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  9. Technology Requirements
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    For online and blended training programs, participants must
                    have:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Reliable internet connection</li>
                    <li>Compatible device (computer, tablet, or smartphone)</li>
                    <li>Required software applications</li>
                    <li>Quiet environment for virtual sessions</li>
                  </ul>
                  <p>
                    Technical support is available but participants are
                    responsible for their connectivity.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  10. Liability and Insurance
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    TCLA-X maintains appropriate insurance coverage for training
                    activities. Participants attend at their own risk and are
                    advised to maintain personal insurance coverage.
                  </p>
                  <p>
                    We are not liable for personal belongings lost or damaged
                    during training sessions.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  11. Contact Information
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    For questions regarding training terms and conditions,
                    please contact:
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <p>
                      <strong>Training Administration</strong>
                    </p>
                    <p>Email: info@tcla-x.com</p>
                    <p>Phone: +254722 319 499</p>
                    <p>Address: Nairobi, Kenya</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TrainingTerms;
